import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import AuthGate from 'src/components/Auth/AuthGate';
import Layout from 'src/components/Layout/Layout';

const LazyFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);

const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<LazyFallback />}>
    <Component />
  </Suspense>
);

// Lazy-loaded page components
const Dashboard = lazy(() => import('src/pages/Dashboard/Dashboard'));
const ActivityCenter = lazy(() => import('src/pages/ActivityCenter/ActivityCenter'));
const CaseDetails = lazy(() => import('src/pages/ActivityCenter/CaseDetails'));
const Products = lazy(() => import('src/pages/Products/Products'));
const ProductLandingPage = lazy(() => import('src/pages/Products/ProductLandingPage'));
const TermLifeInsurance = lazy(() => import('src/pages/Products/TermLife/TermLifeInsurance'));
const GuaranteedUniversalLife = lazy(() => import('src/pages/Products/GUL/GuaranteedUniversalLife'));
const IndexUniversalLife = lazy(() => import('src/pages/Products/IUL/IndexUniversalLife'));
const UniversalLife = lazy(() => import('src/pages/Products/UL/UniversalLife'));
const AssetBasedLTC = lazy(() => import('src/pages/Products/LTC/AssetBasedLTC'));
const PermanentLife = lazy(() => import('src/pages/PermanentLife/PermanentLife'));
const SPL = lazy(() => import('src/pages/Products/SPL/SPL'));
const SubmitCase = lazy(() => import('src/pages/Products/SubmitCase/SubmitCase'));
const RequestIllustration = lazy(() => import('src/pages/Products/RequestIllustration/RequestIllustration'));
const TermConversion = lazy(() => import('src/pages/InsuranceTools/TermConversion/TermConversion'));
const NeedsAnalysis = lazy(() => import('src/pages/InsuranceTools/NeedsAnalysis/NeedsAnalysis'));
const ResourceCenter = lazy(() => import('src/pages/ResourceCenter/ResourceCenter'));
const StateDisclosurePage = lazy(() => import('src/pages/Disclosure/StateDisclosurePage'));
const TermConversionRequest = lazy(() => import('src/pages/TermConversionRequest/TermConversionRequest'));
const PolicyReview = lazy(() => import('src/pages/PolicyReview/PolicyReview'));
const TermReview = lazy(() => import('src/pages/PolicyReview/TermReview/TermReview'));
const PermReview = lazy(() => import('src/pages/PolicyReview/PermReview/PermReview'));

// E-Sign Action route components
const Status = lazy(() => import('src/components/Product/Common/Pages/Status/Status'));
const SignerAuth = lazy(() => import('src/components/Product/Common/Pages/SignerAuth'));
const ConsentToElectronicSignatureWithoutAuth = lazy(
  () => import('src/pages/Products/SPL/steps/ConsentToElectronicSignatureWithoutAuth'),
);
const Sign = lazy(() => import('src/pages/Products/SPL/steps/Sign'));

// Auth landing — handles ?authorization_code redeem
const AuthLandingPage = lazy(() => import('src/pages/AuthLanding/AuthLandingPage'));

const Unauthorized = lazy(() => import('src/pages/Unauthorized/Unauthorized'));
const NewSession = lazy(() => import('src/pages/NewSession/NewSession'));
const PageNotFound = lazy(() => import('src/pages/PageNotFound/PageNotFound'));

export const routes: Array<RouteObject> = [
  // /sso/Authenticate — SSO landing page (no Layout, no AuthGate)
  { path: 'sso/Authenticate', element: withSuspense(AuthLandingPage) },
  {
    element: <AuthGate />,
    children: [
      {
        path: '/',
        element: <Layout />,
        children: [
          // / — Dashboard (advisor-3.0: AdvisorWelcome)
          { index: true, element: withSuspense(Dashboard) },

          // /ActivityCenter — Activity Center
          { path: 'ActivityCenter', element: withSuspense(ActivityCenter) },
          // /ActivityCenter/case-details{typeId}/{caseId} — Case Details
          // e.g. /ActivityCenter/case-detailsC/12345, /ActivityCenter/case-detailsR/17045
          { path: 'ActivityCenter/*', element: withSuspense(CaseDetails) },

          // /activity/request-an-illustration/ — Request an Illustration
          { path: 'activity/request-an-illustration', element: withSuspense(RequestIllustration) },
          { path: 'activity/request-an-illustration/:path', element: withSuspense(RequestIllustration) },

          // /activity/submit-a-case/ — Submit a Case
          { path: 'activity/submit-a-case', element: withSuspense(SubmitCase) },
          { path: 'activity/submit-a-case/:path', element: withSuspense(SubmitCase) },

          // /product/single-premium-whole-life/ — SPL
          { path: 'product/single-premium-whole-life', element: withSuspense(SPL) },
          { path: 'product/single-premium-whole-life/:path', element: withSuspense(SPL) },

          // /insuranceTools/needsAnalysis/ — Needs Analysis
          { path: 'insuranceTools/needsAnalysis', element: withSuspense(NeedsAnalysis) },
          { path: 'insuranceTools/needsAnalysis/:path', element: withSuspense(NeedsAnalysis) },

          // /Products/Landing/:product — Product Landing Page
          { path: 'Products/Landing/:product', element: withSuspense(ProductLandingPage) },
          // /Products/ — Products Page
          { path: 'Products', element: withSuspense(Products) },

          // /PermanentLife — Permanent Life
          { path: 'PermanentLife', element: withSuspense(PermanentLife) },

          // /product/term-life-insurance/ — Term Life Insurance
          { path: 'product/term-life-insurance', element: withSuspense(TermLifeInsurance) },
          { path: 'product/term-life-insurance/:path', element: withSuspense(TermLifeInsurance) },

          // /product/index-Universal-life/ — IUL Estimator
          { path: 'product/index-Universal-life', element: withSuspense(IndexUniversalLife) },
          { path: 'product/index-Universal-life/:path', element: withSuspense(IndexUniversalLife) },

          // /product/universal-life/ — UL Estimator
          { path: 'product/universal-life', element: withSuspense(UniversalLife) },
          { path: 'product/universal-life/:path', element: withSuspense(UniversalLife) },

          // /product/Asset-Based-LTC/ — Asset Based LTC
          { path: 'product/Asset-Based-LTC', element: withSuspense(AssetBasedLTC) },
          { path: 'product/Asset-Based-LTC/:path', element: withSuspense(AssetBasedLTC) },

          // /product/guaranteed-universal-life/ — GUL Quoter
          { path: 'product/guaranteed-universal-life', element: withSuspense(GuaranteedUniversalLife) },
          { path: 'product/guaranteed-universal-life/:path', element: withSuspense(GuaranteedUniversalLife) },

          // /ResourceCenter — Resource Center
          { path: 'ResourceCenter', element: withSuspense(ResourceCenter) },

          // /PolicyReview — Policy Review
          { path: 'policyReview', element: withSuspense(PolicyReview) },
          { path: 'PolicyReview', element: withSuspense(PolicyReview) },

          // /termConversionRequest — Term Conversion Request
          { path: 'termConversionRequest', element: withSuspense(TermConversionRequest) },

          // /insuranceTools/termConversion/ — Term Conversion
          { path: 'insuranceTools/termConversion', element: withSuspense(TermConversion) },
          { path: 'insuranceTools/termConversion/:path', element: withSuspense(TermConversion) },

          // /Quote/TermReview/ — Term Review
          { path: 'Quote/TermReview', element: withSuspense(TermReview) },
          { path: 'Quote/TermReview/:path', element: withSuspense(TermReview) },

          // /Quote/PermReview/ — Perm Review
          { path: 'Quote/PermReview', element: withSuspense(PermReview) },
          { path: 'Quote/PermReview/:path', element: withSuspense(PermReview) },

          // /StateDisclosure — State Disclosure
          { path: 'StateDisclosure', element: withSuspense(StateDisclosurePage) },

          // /unauthorized — Unauthorized
          { path: 'unauthorized', element: withSuspense(Unauthorized) },

          // /ns — New Session (logout)
          { path: 'ns', element: withSuspense(NewSession) },

          // 404
          { path: '*', element: withSuspense(PageNotFound) },
        ],
      },
    ],
  },
  // E-Sign routes — simplified layout without sidebar (isSignWithoutAuth)
  {
    element: <Layout isSignWithoutAuth />,
    children: [
      { path: 'Action/Status', element: withSuspense(Status) },
      {
        path: 'Action/SignNow',
        element: (
          <Suspense fallback={<LazyFallback />}>
            <SignerAuth isSignWithoutAuth />
          </Suspense>
        ),
      },
      { path: 'Action/Consent', element: withSuspense(ConsentToElectronicSignatureWithoutAuth) },
      { path: 'Action/Sign', element: withSuspense(Sign) },
    ],
  },
];
